/**
 * Builds a schema.org FAQPage JSON-LD object from an array of FAQ items.
 *
 * Accepts two formats:
 *  - Flat:    { question: string; answer: string }[]
 *  - Grouped: { title: string; items: { question: string; answer: string }[] }[]
 */

interface FlatFaq {
  question: string;
  answer: string | any; // JSX answers are filtered out
}

interface GroupedFaq {
  title: string;
  items: FlatFaq[];
}

type FaqInput = FlatFaq | GroupedFaq;

function isGrouped(item: FaqInput): item is GroupedFaq {
  return "items" in item && Array.isArray((item as GroupedFaq).items);
}

export function buildFaqSchema(sections: FaqInput[]): Record<string, any> {
  const flat: FlatFaq[] = [];

  sections.forEach((section) => {
    if (isGrouped(section)) {
      section.items.forEach((item) => flat.push(item));
    } else {
      flat.push(section);
    }
  });

  const mainEntity = flat
    .filter((item) => typeof item.answer === "string" && item.question)
    .map((item) => ({
      "@type": "Question",
      name: item.question.trim(),
      acceptedAnswer: {
        "@type": "Answer",
        text: (item.answer as string).trim(),
      },
    }));

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}
